import { useEffect, useRef, useState, type ReactNode } from "react";

interface RevealProps {
  children: ReactNode;
  /** Atraso em segundos, aplicado como transition-delay */
  delay?: number;
  /** Revela imediatamente (sem esperar entrar na viewport) */
  immediate?: boolean;
  className?: string;
}

/**
 * Revelação suave (fade + slide) usando apenas CSS + IntersectionObserver.
 * Substitui o framer-motion nas seções da home para reduzir o bundle inicial.
 */
export function Reveal({ children, delay = 0, immediate = false, className }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(immediate);

  useEffect(() => {
    if (immediate) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "80px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [immediate]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : "translateY(16px)",
        transition: "opacity .5s ease, transform .5s ease",
        transitionDelay: `${delay}s`,
        willChange: visible ? undefined : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

export default Reveal;
